import axios from '../helpers/axios'
import { cartConstants } from './constants'
import store from '../store'
import {
  getCartItemId,
  getOrderItemPrice,
  getOrderItemQuantity,
  getOrderItemSizeLabel,
  getOrderItemType,
  getOrderItemVariantKey,
  getOrderItemWorkType,
  getProductIdFromItem,
  normalizeCartItemMeta,
  normalizeItemsArray,
} from '../utils/orderItemMeta'

const toArrayCartItems = (cartItems) => normalizeItemsArray(cartItems)

const saveLocalCart = (cartItems) => {
  localStorage.setItem('cart', JSON.stringify(toArrayCartItems(cartItems)))
}

const CART_CLIENT_META_KEY = 'cart_configured_items_meta'

const readClientCartMeta = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_CLIENT_META_KEY) || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

const saveClientCartMeta = (meta) => {
  try {
    localStorage.setItem(CART_CLIENT_META_KEY, JSON.stringify(meta || {}))
  } catch (error) {
    // localStorage can be unavailable in private mode. Cart must still work.
  }
}

const getProductIdFromAny = (item) => getProductIdFromItem(item)

const normalizeOrderType = (calculatorData = {}, product = {}) => {
  const source = {
    ...product,
    calculatorData: {
      ...(product?.calculatorData || {}),
      ...(calculatorData || {}),
    },
  }

  return {
    orderItemType: getOrderItemType(source),
    workType: getOrderItemWorkType(source),
  }
}

const buildClientMetaKeys = (productId, calculatorData = {}) => {
  const normalizedProductId = String(productId || '')
  if (!normalizedProductId) return []

  const source = {
    productId: normalizedProductId,
    calculatorData,
  }
  const keys = []
  const variantKey = String(calculatorData?.variantKey || getOrderItemVariantKey(source) || '')
  const { orderItemType, workType } = normalizeOrderType(calculatorData, {})
  const sizeLabel = String(
    calculatorData?.sizeLabel ||
    calculatorData?.selectedSizeLabel ||
    calculatorData?.millingDimensionsLabel ||
    ''
  ).trim()

  if (variantKey) {
    keys.push(`${normalizedProductId}::variant::${variantKey}`)
  }

  if (sizeLabel) {
    keys.push(`${normalizedProductId}::type::${orderItemType}::${workType || 'whole'}::size::${sizeLabel}`)
  }

  keys.push(`${normalizedProductId}::type::${orderItemType}::${workType || 'whole'}`)
  keys.push(`${normalizedProductId}::last`)

  return Array.from(new Set(keys))
}

const getClientMetaCandidates = (productId, meta = {}) => {
  const prefix = `${String(productId || '')}::`

  if (!productId || !meta || typeof meta !== 'object') return []

  const candidates = Object.entries(meta)
    .filter(([key]) => String(key).startsWith(prefix))
    .map(([, value]) => value)
    .filter(Boolean)

  const unique = []
  const seen = new Set()

  candidates.forEach((candidate) => {
    const calc = candidate?.calculatorData || {}
    const signature = [
      candidate?.orderItemType,
      candidate?.workType,
      candidate?.workKey,
      calc?.variantKey,
      candidate?.sizeLabel || calc?.sizeLabel || calc?.selectedSizeLabel,
      candidate?.payablePrice || calc?.payablePrice,
    ].join('::')

    if (seen.has(signature)) return

    seen.add(signature)
    unique.push(candidate)
  })

  return unique
}

const getItemComparablePrice = (item = {}) => getOrderItemPrice(item)
const getMetaComparablePrice = (meta = {}) => getOrderItemPrice(meta)
const getItemComparableSize = (item = {}) => getOrderItemSizeLabel(item)
const getMetaComparableSize = (meta = {}) => getOrderItemSizeLabel(meta)

const findBestClientMetaForCartItem = (item = {}, meta = {}) => {
  const productId = getProductIdFromAny(item)
  const candidates = getClientMetaCandidates(productId, meta)

  if (!candidates.length) return null

  const itemOrderType = normalizeOrderType(item?.calculatorData || {}, item)
  const hasServerType = Boolean(
    item?.orderItemType ||
    item?.workType ||
    item?.workKey ||
    item?.cartItemType ||
    item?.orderItemKind ||
    item?.calculatorData?.orderItemType ||
    item?.calculatorData?.workType ||
    item?.calculatorData?.workKey
  )
  const itemPrice = getItemComparablePrice(item)
  const itemSize = getItemComparableSize(item)
  const itemVariantKey = getOrderItemVariantKey(item)

  const scored = candidates.map((candidate) => {
    const candidateOrderType = normalizeOrderType(candidate?.calculatorData || {}, candidate)
    const candidatePrice = getMetaComparablePrice(candidate)
    const candidateSize = getMetaComparableSize(candidate)
    const candidateVariantKey = getOrderItemVariantKey(candidate)
    let score = 0

    if (itemVariantKey && candidateVariantKey && itemVariantKey === candidateVariantKey) score += 30
    if (hasServerType && candidateOrderType.orderItemType === itemOrderType.orderItemType) score += 8
    if (hasServerType && candidateOrderType.workType === itemOrderType.workType) score += 6
    if (itemPrice > 0 && candidatePrice > 0 && Math.abs(itemPrice - candidatePrice) < 0.01) score += 12
    if (itemSize && candidateSize && itemSize === candidateSize) score += 10

    return { candidate, score }
  }).sort((a, b) => b.score - a.score)

  if (scored[0]?.score > 0) return scored[0].candidate
  if (candidates.length === 1) return candidates[0]

  return null
}

const rememberConfiguredCartItem = (product = {}, calculatorData = {}) => {
  if (!calculatorData || calculatorData?.configured !== true) return

  const productId = getProductIdFromAny(product)
  if (!productId) return

  const meta = readClientCartMeta()
  const normalizedMeta = {
    calculatorData,
    ...normalizeCartItemMeta(product, calculatorData),
  }

  buildClientMetaKeys(productId, calculatorData).forEach((key) => {
    meta[key] = normalizedMeta
  })

  saveClientCartMeta(meta)
}

const mergeClientMetaIntoCartItems = (items = []) => {
  const meta = readClientCartMeta()

  if (!meta || !Object.keys(meta).length) return toArrayCartItems(items)

  return toArrayCartItems(items).map((item) => {
    const calculatorData = item?.calculatorData || {}

    if (calculatorData?.configured === true && (calculatorData?.orderItemType || item?.orderItemType)) {
      return item
    }

    const productId = getProductIdFromAny(item)
    const keys = buildClientMetaKeys(productId, calculatorData)
    const foundMeta = keys.map((key) => meta[key]).find(Boolean) || findBestClientMetaForCartItem(item, meta)

    if (!foundMeta) return item

    const nextCalculatorData = {
      ...(foundMeta.calculatorData || {}),
      ...(item.calculatorData || {}),
      orderItemType: item?.calculatorData?.orderItemType || foundMeta.orderItemType,
      workType: item?.calculatorData?.workType || foundMeta.workType,
      workKey: item?.calculatorData?.workKey || foundMeta.workKey,
      workName: item?.calculatorData?.workName || foundMeta.workName,
      workLabel: item?.calculatorData?.workLabel || foundMeta.workLabel,
      measureMode: item?.calculatorData?.measureMode || foundMeta.measureMode,
      payablePrice: item?.calculatorData?.payablePrice || foundMeta.payablePrice,
      selectedPrice: item?.calculatorData?.selectedPrice || foundMeta.selectedPrice,
      productPrice: item?.calculatorData?.productPrice || foundMeta.productPrice,
      workPrice: item?.calculatorData?.workPrice || foundMeta.workPrice,
      weightKg: item?.calculatorData?.weightKg || foundMeta.weightKg,
      materialDensityKgM3: item?.calculatorData?.materialDensityKgM3 || foundMeta.materialDensityKgM3,
      stoneDensityKgM3: item?.calculatorData?.stoneDensityKgM3 || foundMeta.stoneDensityKgM3,
      selectedSizeKey: item?.calculatorData?.selectedSizeKey || foundMeta.selectedSizeKey,
      sizeLabel: item?.calculatorData?.sizeLabel || foundMeta.sizeLabel,
    }

    return {
      ...item,
      orderItemType: item?.orderItemType || foundMeta.orderItemType,
      workType: item?.workType || foundMeta.workType,
      workKey: item?.workKey || foundMeta.workKey,
      workName: item?.workName || foundMeta.workName,
      workLabel: item?.workLabel || foundMeta.workLabel,
      cartItemType: item?.cartItemType || foundMeta.cartItemType,
      orderItemKind: item?.orderItemKind || foundMeta.orderItemKind,
      orderItemTypeLabel: item?.orderItemTypeLabel || foundMeta.orderItemTypeLabel,
      categoryName: item?.categoryName || foundMeta.categoryName,
      categoryLabel: item?.categoryLabel || foundMeta.categoryLabel,
      sizeLabel: item?.sizeLabel || foundMeta.sizeLabel,
      selectedSizeKey: item?.selectedSizeKey || foundMeta.selectedSizeKey,
      variantKey: item?.variantKey || foundMeta.variantKey,
      measureMode: item?.measureMode || foundMeta.measureMode,
      payablePrice: item?.payablePrice || foundMeta.payablePrice,
      selectedPrice: item?.selectedPrice || foundMeta.selectedPrice,
      price: item?.price || foundMeta.price,
      productPrice: item?.productPrice || foundMeta.productPrice,
      productBasePrice: item?.productBasePrice || foundMeta.productBasePrice,
      workPrice: item?.workPrice || foundMeta.workPrice,
      millingPrice: item?.millingPrice || foundMeta.millingPrice,
      weightKg: item?.weightKg || foundMeta.weightKg,
      materialDensityKgM3: item?.materialDensityKgM3 || foundMeta.materialDensityKgM3,
      stoneDensityKgM3: item?.stoneDensityKgM3 || foundMeta.stoneDensityKgM3,
      millingDimensionsLabel: item?.millingDimensionsLabel || foundMeta.millingDimensionsLabel,
      millingPriceLabel: item?.millingPriceLabel || foundMeta.millingPriceLabel,
      calculatorConfigured: true,
      calculatorData: nextCalculatorData,
    }
  })
}

const getLocalCartItemKey = (item) => String(getCartItemId(item) || '')
const getLocalProductId = (item) => String(getProductIdFromAny(item) || '')
const getPayloadCartItemKey = (payload) => String(payload?.cartItemKey || payload?.cartItemId || payload?.variantKey || payload?.productId || '')
const isConfiguredItem = (calculatorData) => calculatorData?.configured === true

const CART_SCOPE_GLOBAL = 'global'
const CART_SCOPE_SESSION = 'session'

const SESSION_CART_MUTATION_REQUEST = 'SESSION_CART_MUTATION_REQUEST'
const SESSION_CART_MUTATION_SUCCESS = 'SESSION_CART_MUTATION_SUCCESS'
const SESSION_CART_MUTATION_FAILURE = 'SESSION_CART_MUTATION_FAILURE'

const getVisibleProductId = (product = {}) => String(
  getProductIdFromAny(product) ||
  product?._id ||
  product?.id ||
  product?.productId ||
  product?.product ||
  ''
)

const getActiveSalesSessionIdentity = () => {
  const state = store.getState()
  const activeSession = state.officeSalesSession?.activeSession || null
  const officeId = String(
    activeSession?.officeId?._id ||
    activeSession?.officeId ||
    state.office?.currentOfficeId ||
    ''
  ).trim()
  const sessionId = String(activeSession?.sessionId || '').trim().toUpperCase()

  if (!officeId || officeId === 'all' || !sessionId) return null

  return {
    officeId,
    sessionId,
    activeSession,
  }
}

const normalizeSalesSessionCartItem = (item = {}) => {
  const productId = getProductIdFromAny(item)
  if (!productId) return null

  const qty = getOrderItemQuantity(item)
  const price = getOrderItemPrice(item)
  const calculatorData = item?.calculatorData && typeof item.calculatorData === 'object'
    ? item.calculatorData
    : {}

  return {
    ...item,
    productId: String(productId),
    product: String(productId),
    qty,
    quantity: qty,
    purchasedQty: qty,
    payablePrice: price,
    price,
    selectedPrice: item?.selectedPrice || calculatorData?.selectedPrice || price,
    productName: item?.productName || item?.fullName || item?.name || calculatorData?.productName || '',
    name: item?.name || item?.productName || item?.fullName || '',
    fullName: item?.fullName || item?.productName || item?.name || '',
    categoryName: item?.categoryName || item?.categoryLabel || calculatorData?.categoryName || '',
    sizeLabel: getOrderItemSizeLabel(item) || calculatorData?.sizeLabel || calculatorData?.selectedSizeLabel || '',
    orderItemType: getOrderItemType(item),
    workType: getOrderItemWorkType(item),
    workKey: item?.workKey || calculatorData?.workKey || '',
    workName: item?.workName || calculatorData?.workName || '',
    workLabel: item?.workLabel || calculatorData?.workLabel || '',
    calculatorConfigured: item?.calculatorConfigured === true || calculatorData?.configured === true,
    confirmationRequired: item?.confirmationRequired === true || calculatorData?.confirmationRequired === true,
    itemConfirmed: item?.itemConfirmed === true || calculatorData?.itemConfirmed === true,
    calculatorData: {
      ...calculatorData,
      configured: item?.calculatorConfigured === true || calculatorData?.configured === true,
      confirmationRequired: item?.confirmationRequired === true || calculatorData?.confirmationRequired === true,
      itemConfirmed: item?.itemConfirmed === true || calculatorData?.itemConfirmed === true,
      payablePrice: price,
      purchasedQty: qty,
      orderItemType: getOrderItemType(item),
      workType: getOrderItemWorkType(item),
      workKey: item?.workKey || calculatorData?.workKey || '',
      sizeLabel: getOrderItemSizeLabel(item) || calculatorData?.sizeLabel || calculatorData?.selectedSizeLabel || '',
    },
    cartItemId: getCartItemId(item),
    variantKey: getOrderItemVariantKey(item),
  }
}

const normalizeSalesSessionCartItems = (items = []) => {
  return toArrayCartItems(items)
    .map(normalizeSalesSessionCartItem)
    .filter(Boolean)
}

const buildSalesSessionCartTotals = (items = []) => {
  const normalized = normalizeSalesSessionCartItems(items)
  const subtotal = normalized.reduce((sum, item) => {
    return sum + (Number(item.payablePrice || 0) * Number(item.purchasedQty || item.qty || 1))
  }, 0)

  return {
    subtotal,
    discount: 0,
    total: subtotal,
    totalAmount: subtotal,
    itemsCount: normalized.length,
    totalQty: normalized.reduce((sum, item) => sum + (Number(item.purchasedQty || item.qty || 1) || 1), 0),
    updatedAt: new Date().toISOString(),
  }
}

const syncActiveSalesSessionCart = async (items = [], options = {}) => {
  const identity = getActiveSalesSessionIdentity()

  if (!identity) return null

  const normalized = normalizeSalesSessionCartItems(items)
  const totals = buildSalesSessionCartTotals(normalized)

  try {
    const res = await axios.patch(`/office-sales-session/${identity.officeId}/${identity.sessionId}/cart`, {
      items: normalized,
      totals,
      source: options.source || 'cart',
    })

    if (res.status === 200 && res.data?.session) {
      store.dispatch({
        type: 'OFFICE_SALES_SESSION_CART_UPDATE_SUCCESS',
        payload: {
          session: res.data.session,
          message: res.data.message,
        },
      })
    }

    return res.data?.session || null
  } catch (error) {
    store.dispatch({
      type: 'OFFICE_SALES_SESSION_CART_UPDATE_FAILURE',
      payload: {
        error: error?.response?.data?.message || error?.response?.data?.error || error.message,
      },
    })

    return null
  }
}


const getActiveCartScope = () => {
  const state = store.getState()
  return state?.cart?.activeCartScope || CART_SCOPE_GLOBAL
}

const shouldUseSalesSessionCart = () => {
  return !!getActiveSalesSessionIdentity() && getActiveCartScope() === CART_SCOPE_SESSION
}

export const setActiveCartScope = (scope = CART_SCOPE_GLOBAL) => ({
  type: 'SET_CART_ACTIVE_SCOPE',
  payload: scope === CART_SCOPE_SESSION ? CART_SCOPE_SESSION : CART_SCOPE_GLOBAL,
})

const getSessionCartItemsFromState = () => {
  const state = store.getState()
  return toArrayCartItems(state?.cart?.sessionCartItems || [])
}

const getMainCartItemsFromState = () => {
  const state = store.getState()
  return toArrayCartItems(state?.cart?.mainCartItems || state?.cart?.cartItems || [])
}

const dispatchSalesSessionCartItems = (items = [], message = 'Корзина сессии обновлена') => {
  store.dispatch({
    type: 'SESSION_CART_LOCAL_SET',
    payload: {
      cartItems: toArrayCartItems(items),
      message,
    },
  })
}

const buildLocalCartItem = (product = {}, qty = 1, normalizedCalculatorData = null) => {
  const productId = getVisibleProductId(product)
  const normalizedCartMeta = normalizeCartItemMeta(product, normalizedCalculatorData || {})
  const localCartItemId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

  return {
    cartItemId: localCartItemId,
    _id: productId,
    productId,
    product: productId,
    name: product.name || product.productName || product.fullName || '',
    fullName: product.fullName || product.name || product.productName || '',
    img: product.img || product.reducedImage || product.image || product.photo || '',
    qty,
    quantity: qty,
    purchasedQty: qty,
    category: product.category || product.categoryId || normalizedCalculatorData?.categoryKey || null,
    calculatorConfigured: normalizedCalculatorData?.configured === true,
    confirmationRequired: normalizedCalculatorData?.confirmationRequired === true,
    itemConfirmed: normalizedCalculatorData?.itemConfirmed === true,
    calculatorData: {
      ...(normalizedCalculatorData || {}),
      ...normalizedCartMeta,
    },
    ...normalizedCartMeta,
    color: normalizedCalculatorData?.color || '',
    meters: Number(normalizedCalculatorData?.meters || 0) || 0,
    pricePerMeter: Number(normalizedCalculatorData?.pricePerMeter || 0) || 0,
    description: normalizedCalculatorData?.description || '',
    mainImage: normalizedCalculatorData?.mainImage || product.img || product.reducedImage || '',
  }
}

const buildNextCartItemsAfterAdd = (items = [], product = {}, qty = 1, normalizedCalculatorData = null) => {
  const productId = getVisibleProductId(product)
  const currentItems = toArrayCartItems(items)
  const existed = findLocalExistingCartItem(currentItems, productId, normalizedCalculatorData)

  if (existed) {
    return currentItems.map((item) => {
      if (String(getLocalCartItemKey(item)) !== String(getLocalCartItemKey(existed))) return item

      const nextQty = getOrderItemQuantity(item) + qty
      const nextCalculatorData = normalizedCalculatorData
        ? { ...(item.calculatorData || {}), ...normalizedCalculatorData }
        : (item.calculatorData || {})
      const meta = normalizeCartItemMeta(product, nextCalculatorData)

      return {
        ...item,
        qty: nextQty,
        quantity: nextQty,
        purchasedQty: nextQty,
        calculatorConfigured: nextCalculatorData?.configured === true,
        confirmationRequired: nextCalculatorData?.confirmationRequired === true,
        itemConfirmed: nextCalculatorData?.itemConfirmed === true,
        calculatorData: {
          ...nextCalculatorData,
          ...meta,
        },
        ...meta,
        color: nextCalculatorData?.color || item?.color || '',
        meters: Number(nextCalculatorData?.meters || item?.meters || 0) || 0,
        pricePerMeter: Number(nextCalculatorData?.pricePerMeter || item?.pricePerMeter || 0) || 0,
        description: nextCalculatorData?.description || item?.description || '',
        mainImage: nextCalculatorData?.mainImage || item?.mainImage || product?.img || product?.reducedImage || '',
      }
    })
  }

  return [
    ...currentItems,
    buildLocalCartItem(product, qty, normalizedCalculatorData),
  ]
}

const itemMatchesCartPayload = (item, payload = {}) => {
  const itemKey = getLocalCartItemKey(item)
  const itemProductId = getLocalProductId(item)
  const itemVariantKey = getOrderItemVariantKey(item)
  const cartItemId = payload.cartItemId || payload.cartItemKey
  const productId = payload.productId
  const variantKey = payload.variantKey

  if (cartItemId && String(itemKey) === String(cartItemId)) return true
  if (variantKey && String(itemVariantKey) === String(variantKey)) return true
  if (!cartItemId && !variantKey && productId && String(itemProductId) === String(productId)) return true

  return false
}

const findLocalExistingCartItem = (items, productId, calculatorData = null) => {
  const sameProduct = items.filter((item) => String(getLocalProductId(item)) === String(productId))
  if (!sameProduct.length) return null

  if (isConfiguredItem(calculatorData)) {
    const source = { productId, calculatorData }
    const variantKey = String(calculatorData?.variantKey || getOrderItemVariantKey(source) || '')
    const orderItemType = getOrderItemType(source)
    const workType = getOrderItemWorkType(source)
    const sizeLabel = getOrderItemSizeLabel(source)

    return (
      sameProduct.find((item) => {
        const calc = item?.calculatorData || {}
        const itemVariantKey = String(calc?.variantKey || item?.variantKey || getOrderItemVariantKey(item) || '')

        if (variantKey && itemVariantKey) {
          return itemVariantKey === variantKey
        }

        return (
          getOrderItemType(item) === orderItemType &&
          getOrderItemWorkType(item) === workType &&
          getOrderItemSizeLabel(item) === sizeLabel
        )
      }) || null
    )
  }

  return (
    sameProduct.find((item) => {
      const calc = item?.calculatorData || {}
      return !item?.calculatorConfigured && calc?.configured !== true
    }) || null
  )
}

const buildCartPayloadItem = (product, qty, normalizedCalculatorData) => {
  const normalizedCartMeta = normalizeCartItemMeta(product, normalizedCalculatorData || {})

  return {
    product: product._id || product.productId || product.product,
    quantity: qty,
    calculatorData: {
      ...(normalizedCalculatorData || {}),
      ...normalizedCartMeta,
      configured: normalizedCalculatorData?.configured === true,
    },
    orderItemType: normalizedCartMeta.orderItemType,
    workType: normalizedCartMeta.workType,
    workKey: normalizedCartMeta.workKey,
    workName: normalizedCartMeta.workName,
    workLabel: normalizedCartMeta.workLabel,
    cartItemType: normalizedCartMeta.cartItemType,
    orderItemKind: normalizedCartMeta.orderItemKind,
    orderItemTypeLabel: normalizedCartMeta.orderItemTypeLabel,
    payablePrice: normalizedCartMeta.payablePrice,
    selectedPrice: normalizedCartMeta.selectedPrice,
    price: normalizedCartMeta.price,
    productPrice: normalizedCartMeta.productPrice,
    productBasePrice: normalizedCartMeta.productBasePrice,
    workPrice: normalizedCartMeta.workPrice,
    millingPrice: normalizedCartMeta.millingPrice,
    categoryName: normalizedCartMeta.categoryName,
    categoryLabel: normalizedCartMeta.categoryLabel,
    sizeLabel: normalizedCartMeta.sizeLabel,
    selectedSizeKey: normalizedCartMeta.selectedSizeKey,
    variantKey: normalizedCartMeta.variantKey,
    measureMode: normalizedCartMeta.measureMode,
    weightKg: normalizedCartMeta.weightKg,
    materialDensityKgM3: normalizedCartMeta.materialDensityKgM3,
    stoneDensityKgM3: normalizedCartMeta.stoneDensityKgM3,
  }
}


const getSessionCartItemsFromActiveSession = (session = {}) => {
  if (Array.isArray(session?.orderDraftState?.products)) return session.orderDraftState.products
  if (Array.isArray(session?.calc3dState?.cartItems)) return session.calc3dState.cartItems
  if (Array.isArray(session?.catalogState?.selectionItems)) return session.catalogState.selectionItems
  return []
}

const dispatchSessionCartSuccess = (dispatch, items, message = 'Корзина сессии обновлена', extra = {}) => {
  const normalized = toArrayCartItems(items)

  dispatch({
    type: SESSION_CART_MUTATION_SUCCESS,
    payload: {
      ...extra,
      cartItems: normalized,
      updatedCart: normalized,
      message,
      isSessionCart: true,
      keepUpdating: extra.keepUpdating !== false,
    },
  })
}

export const updateCartItemQuantity = (payload) => {
  return async (dispatch) => {
    const cartItemKey = getPayloadCartItemKey(payload)

    try {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_QUANTITY_REQUEST,
        payload: {
          ...payload,
          cartItemKey,
        },
      })

      if (shouldUseSalesSessionCart()) {
        const quantity = Math.max(1, Number(payload?.quantity || payload?.qty || 1) || 1)
        const currentItems = getSessionCartItemsFromState()
        const nextItems = currentItems.map((item) => {
          if (!itemMatchesCartPayload(item, payload)) return item

          return {
            ...item,
            qty: quantity,
            quantity,
            purchasedQty: quantity,
            calculatorData: {
              ...(item.calculatorData || {}),
              purchasedQty: quantity,
            },
          }
        })

        dispatchSessionCartSuccess(
          dispatch,
          nextItems,
          'Количество товара в корзине сессии обновлено',
          { cartItemKey }
        )

        await syncActiveSalesSessionCart(nextItems, { source: 'session-cart-quantity' })
        return true
      }

      const { cart: { mainCartItems, cartItems }, auth: { authenticate } } = store.getState()

      if (authenticate) {
        const res = await axios.post('/user/cart/updateCartItemQuantity', payload)

        if (res.status === 200) {
          const { updatedCart, message } = res.data

          dispatch({
            type: cartConstants.UPDATE_CART_ITEM_QUANTITY_SUCCESS,
            payload: {
              updatedCart,
              message,
              cartItemKey,
            },
          })
        }
      } else {
        const items = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)
        const { quantity } = payload

        const nextItems = items.map((item) => {
          if (!itemMatchesCartPayload(item, payload)) return item

          return {
            ...item,
            qty: quantity,
            quantity,
          }
        })

        saveLocalCart(nextItems)

        dispatch({
          type: cartConstants.UPDATE_CART_ITEM_QUANTITY_SUCCESS,
          payload: {
            updatedCart: nextItems,
            message: 'Количество товара в корзине успешно обновлено',
            cartItemKey,
          },
        })
      }

      await dispatch(getCartItems({ silent: true, forceGlobal: true }))
      return true
    } catch (error) {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_QUANTITY_FAILURE,
        payload: {
          cartItemKey,
          message: error?.response?.data?.message || 'Ошибка обновления количества товара в корзине',
        },
      })

      return false
    }
  }
}

export const updateCartItemConfig = (payload) => {
  return async (dispatch) => {
    const cartItemKey = getPayloadCartItemKey(payload)

    try {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_CONFIG_REQUEST,
        payload: {
          ...payload,
          cartItemKey,
        },
      })

      if (shouldUseSalesSessionCart()) {
        const currentItems = getSessionCartItemsFromState()
        const { calculatorData, quantity } = payload

        const nextItems = currentItems.map((item) => {
          if (!itemMatchesCartPayload(item, payload)) return item

          const nextQty = quantity !== undefined ? Math.max(1, Number(quantity) || 1) : getOrderItemQuantity(item)
          const nextCalculatorData = {
            ...(item.calculatorData || {}),
            ...(calculatorData || {}),
          }
          const meta = normalizeCartItemMeta(item, nextCalculatorData)

          return {
            ...item,
            qty: nextQty,
            quantity: nextQty,
            purchasedQty: nextQty,
            calculatorConfigured: nextCalculatorData?.configured === true,
            confirmationRequired: nextCalculatorData?.confirmationRequired === true,
            itemConfirmed: nextCalculatorData?.itemConfirmed === true,
            calculatorData: {
              ...nextCalculatorData,
              ...meta,
            },
            ...meta,
            color: nextCalculatorData?.color || item?.color || '',
            meters: Number(nextCalculatorData?.meters || item?.meters || 0) || 0,
            pricePerMeter: Number(nextCalculatorData?.pricePerMeter || item?.pricePerMeter || 0) || 0,
            description: nextCalculatorData?.description || item?.description || '',
            mainImage: nextCalculatorData?.mainImage || item?.mainImage || '',
          }
        })

        dispatchSessionCartSuccess(
          dispatch,
          nextItems,
          'Конфигурация позиции корзины сессии обновлена',
          { cartItemKey }
        )

        await syncActiveSalesSessionCart(nextItems, { source: 'session-cart-config' })
        return true
      }

      const { cart: { mainCartItems, cartItems }, auth: { authenticate } } = store.getState()

      if (authenticate) {
        const res = await axios.post('/user/cart/updateCartItemConfig', payload)

        if (res.status === 200) {
          const { updatedCart, message } = res.data

          dispatch({
            type: cartConstants.UPDATE_CART_ITEM_CONFIG_SUCCESS,
            payload: {
              updatedCart,
              message,
              cartItemKey,
            },
          })
        }
      } else {
        const items = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)
        const { cartItemId, calculatorData, quantity } = payload

        const nextItems = items.map((item) => {
          if (String(getLocalCartItemKey(item)) !== String(cartItemId)) return item

          const nextQty = quantity !== undefined ? quantity : getOrderItemQuantity(item)
          const nextCalculatorData = {
            ...(item.calculatorData || {}),
            ...(calculatorData || {}),
          }
          const meta = normalizeCartItemMeta(item, nextCalculatorData)

          return {
            ...item,
            qty: nextQty,
            quantity: nextQty,
            calculatorConfigured: nextCalculatorData?.configured === true,
            confirmationRequired: nextCalculatorData?.confirmationRequired === true,
            itemConfirmed: nextCalculatorData?.itemConfirmed === true,
            calculatorData: {
              ...nextCalculatorData,
              ...meta,
            },
            ...meta,
            color: nextCalculatorData?.color || '',
            meters: Number(nextCalculatorData?.meters || 0) || 0,
            pricePerMeter: Number(nextCalculatorData?.pricePerMeter || 0) || 0,
            description: nextCalculatorData?.description || '',
            mainImage: nextCalculatorData?.mainImage || '',
          }
        })

        saveLocalCart(nextItems)

        dispatch({
          type: cartConstants.UPDATE_CART_ITEM_CONFIG_SUCCESS,
          payload: {
            updatedCart: nextItems,
            message: 'Конфигурация позиции корзины успешно обновлена',
            cartItemKey,
          },
        })
      }

      await dispatch(getCartItems({ silent: true, forceGlobal: true }))
      return true
    } catch (error) {
      dispatch({
        type: cartConstants.UPDATE_CART_ITEM_CONFIG_FAILURE,
        payload: {
          cartItemKey,
          message: error?.response?.data?.message || 'Ошибка обновления конфигурации позиции корзины',
        },
      })

      return false
    }
  }
}

export const addToCart = (product, newQty = 1, type, calculatorData = null) => {
  return async (dispatch) => {
    try {
      const qty = type === 'newQty' ? parseInt(newQty, 10) || 1 : parseInt(newQty, 10) || 1
      const normalizedCalculatorData = calculatorData && typeof calculatorData === 'object'
        ? calculatorData
        : null

      rememberConfiguredCartItem(product, normalizedCalculatorData || {})

      if (shouldUseSalesSessionCart()) {
        dispatch({ type: cartConstants.ADD_TO_CART_REQUEST })

        const currentItems = getSessionCartItemsFromState()
        const nextItems = buildNextCartItemsAfterAdd(currentItems, product, qty, normalizedCalculatorData)

        dispatchSessionCartSuccess(
          dispatch,
          nextItems,
          'Добавлено в корзину сессии'
        )

        await syncActiveSalesSessionCart(nextItems, { source: 'session-cart-add' })
        return true
      }

      const { cart: { mainCartItems, cartItems }, auth } = store.getState()
      const currentItems = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)
      const normalizedCartMeta = normalizeCartItemMeta(product, normalizedCalculatorData || {})

      if (auth.authenticate) {
        dispatch({ type: cartConstants.ADD_TO_CART_REQUEST })

        const payload = {
          cartItems: [
            buildCartPayloadItem(product, qty, normalizedCalculatorData),
          ],
        }

        const res = await axios.post('/user/cart/addtocart', payload)

        if (res.status === 201) {
          dispatch({
            type: cartConstants.ADD_TO_CART_SUCCESS,
            payload: { message: res.data.message, cartItems: currentItems },
          })
        }

        await dispatch(getCartItems({ forceGlobal: true }))
      } else {
        const nextItems = buildNextCartItemsAfterAdd(currentItems, product, qty, normalizedCalculatorData)

        saveLocalCart(nextItems)

        dispatch({
          type: cartConstants.ADD_TO_CART_SUCCESS,
          payload: { cartItems: nextItems, message: 'Добавлено в корзину' },
        })

        await dispatch(getCartItems({ forceGlobal: true }))
      }

      return true
    } catch (payload) {
      dispatch({
        type: cartConstants.ADD_TO_CART_FAILURE,
        payload: { message: payload?.response?.data?.message || 'Ошибка добавления в корзину' },
      })

      return false
    }
  }
}

export const getCartItems = (options = {}) => {
  return async (dispatch, getState) => {
    try {
      const silent = options?.silent === true
      const forceGlobal = options?.forceGlobal === true

      dispatch({
        type: cartConstants.GET_CART_ITEMS_REQUEST,
        payload: {
          silent,
        },
      })

      if (!forceGlobal && shouldUseSalesSessionCart()) {
        const identity = getActiveSalesSessionIdentity()
        const sessionItems = getSessionCartItemsFromState().length
          ? getSessionCartItemsFromState()
          : getSessionCartItemsFromActiveSession(identity?.activeSession)

        dispatch({
          type: 'HYDRATE_CART_FROM_SALES_SESSION',
          payload: {
            officeId: identity?.officeId,
            sessionId: identity?.sessionId,
            cartItems: sessionItems,
          },
        })

        return sessionItems
      }

      const { auth: { authenticate } } = getState()

      if (authenticate) {
        const res = await axios.post('/user/getCartItems')
        if (res.status === 200) {
          const { cartItems } = res.data
          dispatch({
            type: cartConstants.GET_CART_ITEMS_SUCCESS,
            payload: { cartItems: mergeClientMetaIntoCartItems(Array.isArray(cartItems) ? cartItems : []) },
          })

          return cartItems || []
        }
      } else {
        const localCartItems = JSON.parse(localStorage.getItem('cart')) || []
        const normalized = mergeClientMetaIntoCartItems(Array.isArray(localCartItems) ? localCartItems : [])
        dispatch({
          type: cartConstants.GET_CART_ITEMS_SUCCESS,
          payload: { cartItems: normalized },
        })

        return normalized
      }

      return []
    } catch (error) {
      dispatch({
        type: cartConstants.GET_CART_ITEMS_FAILURE,
        payload: {
          error: error?.response?.data?.message || 'Ошибка получения корзины',
        },
      })

      return []
    }
  }
}

export const removeCartItem = (payload) => {
  return async (dispatch) => {
    const normalizedPayload = payload && typeof payload === 'object'
      ? payload
      : { cartItemId: payload }

    try {
      dispatch({ type: cartConstants.REMOVE_CART_ITEM_REQUEST })

      if (shouldUseSalesSessionCart()) {
        const currentItems = getSessionCartItemsFromState()
        const updatedCartItems = currentItems.filter((item) => !itemMatchesCartPayload(item, normalizedPayload))

        dispatchSessionCartSuccess(
          dispatch,
          updatedCartItems,
          'Товар удалён из корзины сессии'
        )

        await syncActiveSalesSessionCart(updatedCartItems, { source: 'session-cart-remove' })
        return true
      }

      const { cart: { mainCartItems, cartItems }, auth: { authenticate } } = store.getState()

      if (authenticate) {
        const res = await axios.post('/user/cart/removeItem', { payload: normalizedPayload })

        if (res.status === 202) {
          dispatch({
            type: cartConstants.REMOVE_CART_ITEM_SUCCESS,
            payload: {
              message: res.data.message,
            },
          })
          await dispatch(getCartItems({ forceGlobal: true }))
        } else {
          const { error } = res.data
          dispatch({
            type: cartConstants.REMOVE_CART_ITEM_FAILURE,
            payload: { error },
          })
        }
      } else {
        const items = toArrayCartItems(mainCartItems?.length ? mainCartItems : cartItems)

        const updatedCartItems = items.filter((item) => !itemMatchesCartPayload(item, normalizedPayload))

        saveLocalCart(updatedCartItems)

        dispatch({
          type: cartConstants.REMOVE_CART_ITEM_SUCCESS,
          payload: {
            message: 'Товар успешно удален с корзины!',
          },
        })

        await dispatch(getCartItems({ forceGlobal: true }))
      }

      return true
    } catch (error) {
      dispatch({
        type: cartConstants.REMOVE_CART_ITEM_FAILURE,
        payload: {
          message: error?.response?.data?.message || 'Что-то пошло не так!',
        },
      })

      return false
    }
  }
}

export const updateCart = () => {
  return async (dispatch) => {
    const { auth } = store.getState()
    let cartItems = localStorage.getItem('cart')
      ? JSON.parse(localStorage.getItem('cart'))
      : null

    if (auth.authenticate) {
      if (cartItems && Array.isArray(cartItems) && cartItems.length > 0) {
        const payload = {
          cartItems: cartItems.map((item) => {
            return buildCartPayloadItem(item, getOrderItemQuantity(item), item.calculatorData || {})
          }),
        }

        localStorage.removeItem('cart')

        const res = await axios.post('/user/cart/addtocart', payload)

        if (res.status === 201 || res.status === 400) {
          await dispatch(getCartItems({ forceGlobal: true }))
        }
      } else {
        localStorage.removeItem('cart')
        await dispatch(getCartItems({ forceGlobal: true }))
      }
    } else if (cartItems) {
      dispatch({
        type: cartConstants.UPDATE_TO_CART_SUCCESS,
        payload: { cartItems },
      })
      await dispatch(getCartItems({ forceGlobal: true }))
    }
  }
}
